import { useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { loadMissingServicesCounts } from "@/billing/services/missing-services-table";
import { MissingServicesCounts } from "@/billing/types/missing-services";

export interface UseMissingServicesCountsResult {
  /** Undefined while loading, and after a failure, so the caller can show a dash instead of a zero. */
  counts: MissingServicesCounts | undefined;
}

/**
 * Loads the practice-wide counts for the Missing Services summary cards.
 *
 * Depends on `practiceId` and `refreshKey` alone, so turning the table's page never refetches these
 * and never changes what the cards show. Bump `refreshKey` after a write that changes a count (for
 * example dismissing an Encounter) to force a reload. Failures go to Sentry and leave `counts`
 * undefined.
 */
export function useMissingServicesCounts(
  practiceId: string | undefined,
  refreshKey = 0,
): UseMissingServicesCountsResult {
  const medplum = useMedplum();
  const [counts, setCounts] = useState<MissingServicesCounts | undefined>();
  const latestRequestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!practiceId?.trim()) {
      latestRequestIdRef.current += 1;
      setCounts(undefined);
      return;
    }

    const requestId = ++latestRequestIdRef.current;

    void (async () => {
      const result = await loadMissingServicesCounts(medplum, practiceId);
      if (requestId !== latestRequestIdRef.current || !isMountedRef.current) {
        return;
      }

      if (result.ok) {
        setCounts(result.data);
      } else {
        const loadError = new Error(`Missing Services counts failed: ${result.reason}`);
        setCounts(undefined);
        Sentry.captureException(loadError, { extra: { details: result.details } });
      }
    })();
  }, [medplum, practiceId, refreshKey]);

  return { counts };
}
